/**
 * Persisted sign-in intent — the two things the external-browser LogTo
 * handoff loses when the AuthDialog closes or the app restarts before the
 * deep link comes back:
 *
 * 1. WHY the sign-in was opened (the `source` property on `auth:sign_in`).
 * 2. That the sign-in was supposed to END IN A TRIAL. Field data: 83/88
 *    successful trials started under a minute after registration and none
 *    ever later, yet 60% of explicit "Start Free Trial" clicks produced no
 *    trial — the in-memory intent died with the dialog/process. Persisting it
 *    lets the app RE-OFFER the trial dialog once the user is signed in
 *    (a consent surface — a trial is never auto-started from a stale intent).
 *
 * Renderer-only localStorage; every access is try/catch-wrapped (storage can
 * be unavailable) and every reader tolerates corrupt values.
 */

const TRIAL_INTENT_KEY = 'sql-studio-pending-trial-intent';
const AUTH_SOURCE_KEY = 'sql-studio-auth-intent-source';
/**
 * Set on EVERY completed sign-in (see authStore's SIGNED_IN handler), not only
 * the ones that began with "Start Free Trial". Field data (1.4.25, 09-2026):
 * 7 of 11 registrations came through a "Sign in" surface — Settings → License,
 * the trial dialog's "Already a Pro subscriber?" link, the start-page card's
 * "Have Pro? Sign in" — and none of those users started a trial, because
 * nothing offered one once their account resolved to no entitlement. This
 * marker lets the trial dialog re-offer ONCE after any such sign-in.
 */
const POST_SIGNIN_OFFER_KEY = 'sql-studio-post-signin-trial-offer';

/**
 * A trial intent older than this is dropped unconsumed: a sign-in weeks after
 * an abandoned trial click (e.g. to manage a subscription) must not surface a
 * surprise trial offer.
 */
export const TRIAL_INTENT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The post-sign-in offer is only meaningful right after the sign-in that set
 * it: it is consumed as soon as entitlement resolves to "no Pro", and if the
 * account turned out to have Pro it must not resurface weeks later when that
 * subscription lapses.
 */
export const POST_SIGNIN_OFFER_MAX_AGE_MS = 60 * 60 * 1000;

/** Which surface opened the sign-in flow (low-cardinality analytics value). */
export const AUTH_INTENT_SOURCES = [
  'trial_dialog',
  'trial_dialog_signin',
  'trial_card_signin',
  'upgrade_dialog',
  'settings',
  'footer',
  'reauth',
] as const;

export type AuthIntentSource = (typeof AUTH_INTENT_SOURCES)[number];

/** Remember which surface is about to open the sign-in flow. */
export function recordAuthIntentSource(source: AuthIntentSource): void {
  try {
    window.localStorage.setItem(AUTH_SOURCE_KEY, source);
  } catch {
    // Storage unavailable — the sign_in event falls back to 'unknown'.
  }
}

/**
 * Read-and-clear the recorded sign-in source for the `auth:sign_in` event.
 * Unknown/corrupt/absent values come back as 'unknown'.
 */
export function consumeAuthIntentSource(): AuthIntentSource | 'unknown' {
  try {
    const raw = window.localStorage.getItem(AUTH_SOURCE_KEY);
    window.localStorage.removeItem(AUTH_SOURCE_KEY);
    return (AUTH_INTENT_SOURCES as readonly string[]).includes(raw ?? '')
      ? (raw as AuthIntentSource)
      : 'unknown';
  } catch {
    return 'unknown';
  }
}

/** Remember that the sign-in flow being opened is meant to start a trial. */
export function recordPendingTrialIntent(now: number = Date.now()): void {
  try {
    window.localStorage.setItem(TRIAL_INTENT_KEY, String(now));
  } catch {
    // Storage unavailable — the in-session flow still works; only the
    // restart-survival net is lost.
  }
}

/** Drop the persisted trial intent (trial started, or the user declined). */
export function clearPendingTrialIntent(): void {
  try {
    window.localStorage.removeItem(TRIAL_INTENT_KEY);
  } catch {
    // ignore
  }
}

/**
 * Why a completed sign-in should be followed by a trial offer.
 * - `trial_intent`: the user pressed "Start Free Trial" and the sign-in was a
 *   step of it (persisted by recordPendingTrialIntent).
 * - `sign_in`: the user signed in through a surface that never promised a
 *   trial (Settings, "Have Pro? Sign in", …) and the account has no Pro.
 */
export type TrialReofferReason = 'trial_intent' | 'sign_in';

/**
 * Which sign-in surfaces get the post-sign-in offer. Only the upgrade dialog
 * is excluded: that sign-in is a step of a Stripe checkout, and a trial dialog
 * popping over a purchase in progress would only get in the way.
 */
export function shouldOfferTrialAfterSignIn(source: AuthIntentSource | 'unknown'): boolean {
  return source !== 'upgrade_dialog';
}

/** Remember that a sign-in just completed and a one-time trial offer is owed. */
export function recordPostSignInTrialOffer(now: number = Date.now()): void {
  try {
    window.localStorage.setItem(POST_SIGNIN_OFFER_KEY, String(now));
  } catch {
    // Storage unavailable — no re-offer, nothing else is affected.
  }
}

function consumeFreshTimestamp(key: string, maxAgeMs: number, now: number): boolean {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return false;
    window.localStorage.removeItem(key);
    const recordedAt = Number(raw);
    if (!Number.isFinite(recordedAt)) return false;
    return now - recordedAt >= 0 && now - recordedAt <= maxAgeMs;
  } catch {
    return false;
  }
}

/**
 * Read-and-clear BOTH persisted re-offer records and say which one applies.
 * A record counts only while fresh (TRIAL_INTENT_MAX_AGE_MS resp.
 * POST_SIGNIN_OFFER_MAX_AGE_MS) — stale or corrupt records are removed and
 * reported as absent either way (one re-offer per record, ever). The explicit
 * trial intent wins over the plain sign-in marker when both are present.
 */
export function consumeFreshTrialReoffer(now: number = Date.now()): TrialReofferReason | null {
  const trialIntent = consumeFreshTimestamp(TRIAL_INTENT_KEY, TRIAL_INTENT_MAX_AGE_MS, now);
  const signIn = consumeFreshTimestamp(POST_SIGNIN_OFFER_KEY, POST_SIGNIN_OFFER_MAX_AGE_MS, now);
  if (trialIntent) return 'trial_intent';
  if (signIn) return 'sign_in';
  return null;
}
